/**
 * BrandSaveCoordinator
 *
 * Framework-agnostic coordinator that owns brand-settings persistence semantics:
 *
 * - Single in-flight save: while one HTTP request is in flight, additional
 *   `enqueue` calls coalesce into a pending patch. When the in-flight call
 *   resolves, one composed request is fired with the merged pending patch.
 * - Debounced auto-flush: per-call debounce window (default 600ms) batches
 *   autosave-style callers into a single save. Current Settings color controls
 *   preview locally and persist through explicit immediate saves.
 * - Revision tracking: each enqueue bumps `pendingRev`. Each fired request
 *   captures its revision; on response, the coordinator skips the canonical
 *   merge if a newer request has already been applied (stale-response
 *   suppression).
 * - Field-skip merge: canonical responses are applied field-by-field, skipping
 *   keys that still exist in the local pending patch.
 * - Deep merge of `brandThemeConfig.seeds`: changes to different seed slots
 *   within the same window all persist (e.g. `accent.light` and `card.dark`).
 * - `undefined` semantics preserved as "no change"; `null` preserved as
 *   "explicit clear" across coalescing and DTO submission.
 * - Persistence telemetry via Mixpanel (brand_settings_saved / brand_settings_save_failed).
 */

import type {
  AmbientFieldStops,
  BrandSettings,
  BrandThemeConfig,
  BrandThemeSeedKey,
  BrandThemeSeedPair,
} from '@digital-twins/shared-types'
import { track, AnalyticsEvents } from '@/lib/services/analytics'

export type BrandPatch = Partial<BrandSettings>

export interface BrandSaveCoordinatorOptions {
  /** HTTP call. Must throw on failure; coordinator handles in-flight + retry. */
  send: (patch: BrandPatch) => Promise<BrandSettings>
  /** Callback invoked with canonical server state after a successful save. */
  onCanonical?: (canonical: BrandSettings, appliedPatch: BrandPatch) => void
  /** Callback invoked on save failure with the patch that failed. */
  onError?: (error: unknown, attemptedPatch: BrandPatch) => void
  /** Debounce window for `enqueue` calls. Defaults to 600ms. */
  debounceMs?: number
  /** Override clock for tests. */
  now?: () => number
}

export interface BrandSaveCoordinatorState {
  pendingRev: number
  lastAppliedRev: number
  isPersisting: boolean
  hasPending: boolean
}

/** Deep merge two BrandThemeConfig values, preserving `undefined`=no-change and `null`=clear. */
export function mergeBrandThemeConfig(
  base: BrandThemeConfig | null | undefined,
  patch: BrandThemeConfig | null | undefined,
): BrandThemeConfig | null | undefined {
  if (patch === undefined) return base
  if (patch === null) return null
  if (base === null || base === undefined) {
    return patch
  }
  const merged: BrandThemeConfig = {
    ...base,
    ...patch,
    version: patch.version ?? base.version,
  }
  if (patch.seeds !== undefined) {
    if (patch.seeds === null) {
      merged.seeds = null
    } else {
      const baseSeeds = base.seeds ?? {}
      const mergedSeeds: Partial<Record<BrandThemeSeedKey, BrandThemeSeedPair>> = { ...baseSeeds }
      for (const [key, patchPair] of Object.entries(patch.seeds) as Array<[BrandThemeSeedKey, BrandThemeSeedPair | undefined]>) {
        if (patchPair === undefined) continue
        const basePair = baseSeeds[key] ?? {}
        // Build slot-by-slot, omitting undefined keys so the resulting shape
        // matches the optional fields on BrandThemeSeedPair (no `{ dark: undefined }`).
        const nextPair: BrandThemeSeedPair = {}
        const light = patchPair.light === undefined ? basePair.light : patchPair.light
        const dark = patchPair.dark === undefined ? basePair.dark : patchPair.dark
        if (light !== undefined) nextPair.light = light
        if (dark !== undefined) nextPair.dark = dark
        mergedSeeds[key] = nextPair
      }
      merged.seeds = mergedSeeds
    }
  } else if (base.seeds !== undefined) {
    merged.seeds = base.seeds
  }
  if (patch.ambientField !== undefined) {
    if (patch.ambientField === null) {
      merged.ambientField = null
    } else {
      const baseAmbient = base.ambientField ?? null
      // Slot-by-slot: a patch touching only `light` must not clobber `dark`
      // (and vice versa) while pending patches coalesce ahead of a save.
      const light: AmbientFieldStops | null =
        patch.ambientField.light === undefined
          ? (baseAmbient?.light ?? null)
          : patch.ambientField.light
      const dark: AmbientFieldStops | null =
        patch.ambientField.dark === undefined
          ? (baseAmbient?.dark ?? null)
          : patch.ambientField.dark
      const gradients =
        patch.ambientField.gradients === undefined
          ? baseAmbient?.gradients
          : patch.ambientField.gradients
      merged.ambientField = gradients === undefined ? { light, dark } : { light, dark, gradients }
    }
  } else if (base.ambientField !== undefined) {
    merged.ambientField = base.ambientField
  }
  if (patch.chartPaletteId === undefined) {
    merged.chartPaletteId = base.chartPaletteId
  }
  return merged
}

/**
 * Merge a new patch into the accumulating pending patch.
 *
 * Semantics:
 * - keys absent in `patch` -> retain existing pending key
 * - `undefined` in `patch` -> treated as absent (skip)
 * - `null` in `patch` -> explicit clear; preserved in merged patch
 * - `brandThemeConfig` is deep-merged via `mergeBrandThemeConfig`
 */
export function mergeBrandPatch(base: BrandPatch, patch: BrandPatch): BrandPatch {
  const out: BrandPatch = { ...base }
  for (const [k, v] of Object.entries(patch) as Array<[keyof BrandSettings, unknown]>) {
    if (v === undefined) continue
    if (k === 'brandThemeConfig') {
      const merged = mergeBrandThemeConfig(
        base.brandThemeConfig as BrandThemeConfig | null | undefined,
        patch.brandThemeConfig as BrandThemeConfig | null | undefined,
      )
      // explicit assign even when merged === null (clear) or undefined (no change after merge).
      if (merged === undefined) {
        delete (out as Record<string, unknown>).brandThemeConfig
      } else {
        (out as Record<string, unknown>).brandThemeConfig = merged
      }
      continue
    }
    (out as Record<string, unknown>)[k as string] = v
  }
  return out
}

/** Returns the canonical settings with fields skipped where the pending patch still has them. */
export function applyCanonicalSkippingPending(
  current: BrandSettings,
  canonical: BrandSettings,
  pending: BrandPatch,
): BrandSettings {
  const next: BrandSettings = { ...current }
  const takeCanonical = <K extends keyof BrandSettings>(key: K) => {
    next[key] = canonical[key]
  }
  for (const k of Object.keys(canonical) as Array<keyof BrandSettings>) {
    if (k in pending && (pending as Record<string, unknown>)[k as string] !== undefined) {
      continue
    }
    takeCanonical(k)
  }
  return next
}

export class BrandSaveCoordinator {
  private send: BrandSaveCoordinatorOptions['send']
  private onCanonical?: BrandSaveCoordinatorOptions['onCanonical']
  private onError?: BrandSaveCoordinatorOptions['onError']
  private debounceMs: number
  private now: () => number

  private pending: BrandPatch = {}
  private pendingRev = 0
  private lastAppliedRev = 0
  private inFlightRev: number | null = null
  private inFlight: Promise<BrandSettings> | null = null
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  /** Resolvers waiting for the next composed save to complete. */
  private waiters: Array<{
    resolve: (s: BrandSettings) => void
    reject: (e: unknown) => void
    rev: number
  }> = []
  /** Counts patches coalesced since the last fired request. */
  private coalescedCount = 0
  private listeners = new Set<(state: BrandSaveCoordinatorState) => void>()

  constructor(options: BrandSaveCoordinatorOptions) {
    this.send = options.send
    this.onCanonical = options.onCanonical
    this.onError = options.onError
    this.debounceMs = options.debounceMs ?? 600
    this.now = options.now ?? Date.now
  }

  /** Snapshot of the current pending patch. Used by consumers (e.g. BrandProvider)
   * to skip canonical-response merges for keys still queued locally. */
  getPending(): BrandPatch {
    return { ...this.pending }
  }

  getState(): BrandSaveCoordinatorState {
    return {
      pendingRev: this.pendingRev,
      lastAppliedRev: this.lastAppliedRev,
      isPersisting: this.inFlight !== null || this.debounceTimer !== null || this.hasPendingKeys(),
      hasPending: this.hasPendingKeys(),
    }
  }

  subscribe(listener: (state: BrandSaveCoordinatorState) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notify() {
    const state = this.getState()
    for (const l of this.listeners) {
      l(state)
    }
  }

  private hasPendingKeys(): boolean {
    for (const k of Object.keys(this.pending)) {
      if ((this.pending as Record<string, unknown>)[k] !== undefined) return true
    }
    return false
  }

  /**
   * Enqueue a patch. Returns a promise that resolves with the canonical
   * BrandSettings after the next composed save completes.
   *
   * Pass `{ immediate: true }` to bypass the debounce timer (used by Apply/Save/Detect
   * paths that should not wait for the debounce window).
   */
  enqueue(patch: BrandPatch, opts?: { immediate?: boolean }): Promise<BrandSettings> {
    this.pending = mergeBrandPatch(this.pending, patch)
    this.pendingRev += 1
    this.coalescedCount += 1
    const rev = this.pendingRev

    const promise = new Promise<BrandSettings>((resolve, reject) => {
      this.waiters.push({ resolve, reject, rev })
    })

    if (opts?.immediate) {
      this.cancelDebounce()
      // Fire on next microtask to allow further synchronous enqueues in same tick to coalesce.
      void Promise.resolve().then(() => this.maybeFire())
    } else {
      this.scheduleDebounce()
    }

    this.notify()
    return promise
  }

  /**
   * Cancel any debounce timer and await a final composed save. If a save is
   * already in flight, the queued patch is fired immediately after it resolves.
   * If no pending patch exists and no in-flight save, resolves immediately.
   */
  async flush(): Promise<void> {
    this.cancelDebounce()
    if (!this.hasPendingKeys() && this.inFlight === null) {
      return
    }
    if (this.hasPendingKeys()) {
      // Fire as soon as we can.
      void this.maybeFire()
    }
    // Wait for both the current in-flight (if any) and the queued follow-up.
    while (this.inFlight !== null || this.hasPendingKeys()) {
      try {
        await this.inFlight
      } catch {
        // Errors are surfaced via the original enqueue() promises and onError.
        return
      }
      if (this.hasPendingKeys()) {
        void this.maybeFire()
      }
    }
  }

  private scheduleDebounce() {
    this.cancelDebounce()
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      void this.maybeFire()
    }, this.debounceMs)
  }

  private cancelDebounce() {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  private async maybeFire() {
    if (this.inFlight !== null) return
    if (!this.hasPendingKeys()) return

    const patch = this.pending
    this.pending = {}
    const firedRev = this.pendingRev
    this.inFlightRev = firedRev
    const coalescedThisRequest = this.coalescedCount
    this.coalescedCount = 0
    const waiters = this.waiters.filter((w) => w.rev <= firedRev)
    this.waiters = this.waiters.filter((w) => w.rev > firedRev)

    const patchKeys = Object.keys(patch)
    const startedAt = this.now()
    const promise = this.send(patch)
    this.inFlight = promise
    this.notify()

    try {
      const canonical = await promise
      const durationMs = this.now() - startedAt
      track(AnalyticsEvents.BRAND_SETTINGS_SAVED, {
        patchKeys,
        coalescedUpdates: coalescedThisRequest,
        durationMs,
      })
      // Stale-response suppression: only apply if firedRev is the freshest
      // applied response. Earlier in-flight responses to a same coordinator
      // would already be serialized through the single-in-flight rule, but we
      // still gate on rev ordering for safety.
      if (firedRev >= this.lastAppliedRev) {
        this.lastAppliedRev = firedRev
        this.onCanonical?.(canonical, patch)
      }
      for (const w of waiters) w.resolve(canonical)
      return canonical
    } catch (err) {
      const durationMs = this.now() - startedAt
      track(AnalyticsEvents.BRAND_SETTINGS_SAVE_FAILED, {
        patchKeys,
        coalescedUpdates: coalescedThisRequest,
        durationMs,
        error: err instanceof Error ? err.message : String(err),
      })
      this.onError?.(err, patch)
      for (const w of waiters) w.reject(err)
      // Intentionally do NOT rethrow: the rejection is fully consumed by
      // waiter promises (original enqueue callers) and onError. Rethrowing
      // would surface as unhandledrejection for the internal side-channel
      // callers (`void this.maybeFire()`), which are scheduling-only and have
      // no caller to attach .catch to.
    } finally {
      this.inFlight = null
      this.inFlightRev = null
      // If more patches arrived during the in-flight save, fire one composed follow-up.
      if (this.hasPendingKeys()) {
        void this.maybeFire()
      }
      this.notify()
    }
  }
}
